<template>
  <div
    ref="listRef"
    :class="classes(n())"
  >
    <!-- 列表内容插槽 -->
    <slot />

    <!-- 加载状态 -->
    <div
      v-if="loading"
      :class="n('loading')"
    >
      <slot name="loading">
        <LoadingIcon
          :class="n('loading-icon')"
          width="32"
          height="32"
        />
        <span
          v-if="loadingText"
          :class="n('loading-text')"
        >
          {{ loadingText }}
        </span>
      </slot>
    </div>

    <!-- 加载完成状态 -->
    <div
      v-if="finished"
      :class="n('finished')"
    >
      <slot name="finished">
        <span
          v-if="finishedText"
          :class="n('finished-text')"
        >
          {{ finishedText }}
        </span>
      </slot>
    </div>

    <!-- 加载失败状态 -->
    <div
      v-if="error"
      :class="n('error')"
    >
      <slot name="error">
        <span
          v-if="errorText"
          :class="n('error-text')"
          @click="handleRetry"
        >
          {{ errorText }}
        </span>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { LoadingIcon } from '@tencent/wphk-design-icons';

const { name, n, classes } = createNamespace('list');

const DEFAULT_OFFSET = 50;

export default defineComponent({
  name,
  components: {
    LoadingIcon,
  },
  props,
  emits,
  setup(props, { emit }) {
    const listRef = ref<HTMLElement>();
    let scroller: HTMLElement | null = null;

    // 获取滚动容器
    const getScroller = (element: HTMLElement): HTMLElement => {
      // 使用 parentNode 以兼容 lite
      let parent = element.parentNode as HTMLElement;
      while (parent) {
        const style = getComputedStyle(parent);
        if (
          // lite 下 scroll-view 是滚动容器
          parent.tagName === 'scroll-view'
          || style.overflow === 'auto'
          || style.overflow === 'scroll'
          || style.overflowY === 'auto'
          || style.overflowY === 'scroll'
        ) {
          return parent;
        }
        parent = parent.parentNode as HTMLElement;
      }
      return document.documentElement;
    };

    // 检查是否需要触发加载
    const checkLoad = () => {
      if (props.loading || props.finished || props.error) {
        return;
      }

      if (!listRef.value) {
        return;
      }

      // 获取当前的滚动容器
      let scrollTop: number;
      let scrollHeight: number;
      let clientHeight: number;

      if (scroller === document.documentElement) {
        scrollTop = document.documentElement.scrollTop;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = document.documentElement.clientHeight;
      } else {
        scrollTop = scroller.scrollTop;
        scrollHeight = scroller.scrollHeight;
        clientHeight = scroller.clientHeight;
      }

      // 检查是否接近底部
      if (scrollTop + clientHeight >= scrollHeight - DEFAULT_OFFSET) {
        emit('update:loading', true);
        emit('load');
      }
    };

    // 滚动事件处理
    const handleScroll = () => {
      checkLoad();
    };

    // 重试处理
    const handleRetry = () => {
      emit('update:error', false);
      nextTick(() => {
        checkLoad();
      });
    };

    // 移除滚动监听
    const removeScrollListener = () => {
      if (scroller) {
        scroller.removeEventListener('scroll', handleScroll);
        scroller = null;
      }
    };

    // 监听loading状态变化，加载完成后重新检查是否需要继续加载，避免新增数据不够撑满屏幕
    watch(() => [props.loading, props.finished], checkLoad);

    onMounted(() => {
      scroller = getScroller(listRef.value);
      scroller.addEventListener('scroll', handleScroll);
      checkLoad();
    });

    onUnmounted(() => {
      removeScrollListener();
    });

    return {
      n,
      classes,
      listRef,
      handleRetry,
    };
  },
});
</script>
